import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { america, findMission, missionAt, missionPath } from '@/content/missions'
import { isSharedHash, usePrefs } from './prefs'

/**
 * Keeps the home page's address and its mission together: arriving at /cbp
 * chooses CBP, and choosing a mission moves the address to its own, or to
 * the site root for the nation. The address is replaced rather than added
 * to the history, so Back leaves the page instead of stepping through
 * missions.
 */
export function useMissionAddress() {
  const { prefs, set } = usePrefs()
  const { pathname, hash } = useLocation()
  const navigate = useNavigate()
  const lastPath = useRef<string | null>(null)
  const lastMission = useRef<string | null>(null)

  // One sync, so the address and the mission never pull against each other.
  // Arriving at an agency's address chooses that agency; arriving at the site
  // root shows the mission already chosen; choosing a mission moves the
  // address to its own.
  useEffect(() => {
    const pathChanged = lastPath.current !== pathname
    const missionChanged = lastMission.current !== prefs.mission
    lastPath.current = pathname
    lastMission.current = prefs.mission
    const addressed = missionAt(pathname)
    if (pathChanged && addressed && addressed.id !== america.id) {
      if (addressed.id !== prefs.mission) {
        lastMission.current = addressed.id
        set('mission', addressed.id)
      }
      return
    }
    if (!pathChanged && !missionChanged) return
    const path = missionPath(findMission(prefs.mission) ?? america)
    // A shared design's hash belongs to the address it was opened at; choosing
    // another mission leaves it behind, so a reload keeps the visitor's changes.
    const keep = isSharedHash(hash) ? '' : hash
    if (path !== pathname) navigate({ pathname: path, hash: keep }, { replace: true })
  }, [pathname, hash, prefs.mission, set, navigate])
}
